import * as React from "react";
import {
  makeStyles,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectProps,
  MenuItem,
  Typography,
} from "@material-ui/core";

import { FunnelIcon } from "../funnel-icon";

// import classnames from "classnames";

// material-ui and styles
import { greys, colors, helpers } from "@styles/color.styles";

export interface MultiFilterProps {
  onSelect: (teamId?: string, billingGroupId?: string) => void;

  selectProps?: SelectProps;
  teamOptions: { label: string; value: string }[];
  billingGroupOptions: { label: string; value: string }[];

  billingGroupId?: string;
  teamId?: string;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
  },
  filterLabel: {
    color: colors.blueDusk.standard,
    fontWeight: 700,
  },
  multiFilterLabel: {
    display: "flex",
    alignItems: "center",
    marginRight: 5,
  },
  content: {
    display: "flex",
    alignItems: "center",
  },
  forms: {
    display: "flex",
    alignItems: "center",
  },
  selectContainer: {
    width: 150,
  },
  selectMargin: {
    margin: "0 10px",
    color: greys.slate,
  },
  inputLabel: {
    padding: "0 12px",
    marginTop: -14,
    color: greys.slate,
  },
  outlinedInputRoot: {
    height: 28,
    "&:before": {
      border: `1px solid ${greys.borderGrey}`,
    },
    "&:hover": {
      "&:before": {
        border: `1px solid ${greys.borderGrey}`,
      },
    },
  },
  select: {
    padding: "3px 5px",
  },
  chip: {
    background: colors.blueDusk.standard,
    color: greys.white,
    "&:focus": {
      background: colors.blueDusk.standard,
    },
  },
  deleteIcon: {
    color: greys.white,
    "&:hover": {
      color: helpers.opacity(greys.white, 0.4),
    },
  },
  bold: {
    fontWeight: "bold",
  },
  chipLabel: {
    lineHeight: "22px",
  },
});

export const MultiFilter = (props: MultiFilterProps) => {
  const classes = useStyles();

  const [teamId, setTeamId] = React.useState<string | undefined>(props.teamId);
  const [billingGroupId, setBillingGroupId] = React.useState<string | undefined>(
    props.billingGroupId,
  );

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const clearFilter = () => {
    setTeamId(undefined);
    setBillingGroupId(undefined);
    props.onSelect(undefined, undefined);
  };

  const maybeGetSelectedFilterLabel = () => {
    return {
      teamLabel: props.teamOptions.find((o) => o.value === teamId)?.label,
      billingGroupLabel: props.billingGroupOptions.find((o) => o.value === billingGroupId)?.label,
    };
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const renderTeamFilter = () => {
    return (
      <FormControl className={classes.selectContainer}>
        <InputLabel className={classes.inputLabel} shrink={false}>
          Teams
        </InputLabel>
        <Select
          {...props.selectProps}
          classes={{ root: classes.outlinedInputRoot, select: classes.select }}
          value={teamId}
          onChange={(e) => {
            const newTeamId = e.target.value as string;
            setTeamId(newTeamId);
            props.onSelect(newTeamId, undefined);
          }}
          variant="outlined"
          disabled={!props.teamOptions.length}
          MenuProps={{
            disableScrollLock: true,
          }}
        >
          {props.teamOptions.map((option) => (
            <MenuItem key={`team-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderBillingGroupFilter = () => {
    return (
      <FormControl className={classes.selectContainer}>
        <InputLabel className={classes.inputLabel} shrink={false}>
          Plans
        </InputLabel>
        <Select
          {...props.selectProps}
          classes={{ root: classes.outlinedInputRoot, select: classes.select }}
          value={billingGroupId}
          onChange={(e) => {
            const newBillingGroupId = e.target.value as string;
            setBillingGroupId(newBillingGroupId);
            props.onSelect(undefined, newBillingGroupId);
          }}
          variant="outlined"
          disabled={!props.billingGroupOptions.length}
          MenuProps={{
            disableScrollLock: true,
          }}
        >
          {props.billingGroupOptions.map((option) => (
            <MenuItem key={`subscription-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderSelectForms = () => {
    return (
      <div className={classes.forms}>
        {renderTeamFilter()}
        <Typography variant="body1" className={classes.selectMargin}>
          or
        </Typography>
        {renderBillingGroupFilter()}
      </div>
    );
  };

  const renderPill = () => {
    const selectedValues = maybeGetSelectedFilterLabel();

    // const chipLabel = `${
    //   selectedValues.teamLabel ? "Team" : selectedValues.billingGroupLabel ? "Subscription" : ""
    // }: ${selectedValues.teamLabel || selectedValues.billingGroupLabel}`;

    const groupChipLabel = selectedValues.billingGroupLabel ? "Plan" : "";

    const chipLabel = (
      <div className={classes.chipLabel}>
        <span>{selectedValues.teamLabel ? "Team" : groupChipLabel}</span>
        {": "}
        <span className={classes.bold}>
          {selectedValues.teamLabel || selectedValues.billingGroupLabel}
        </span>
      </div>
    );
    return (
      <Chip
        label={chipLabel}
        onDelete={(_event) => clearFilter()}
        classes={{ root: classes.chip, deleteIcon: classes.deleteIcon }}
      />
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.multiFilterLabel}>
        <FunnelIcon style={{ marginRight: 10 }} color={colors.blueDusk.standard} />
        <Typography variant="body1" className={classes.filterLabel}>
          {teamId || billingGroupId ? "Filtered" : "Filter"} by:
        </Typography>
      </div>
      {teamId || billingGroupId ? renderPill() : renderSelectForms()}
    </div>
  );
};
